import { cn } from '../cn.js';

export interface TabItem {
  key: string;
  label: string;
  /** Shown beside the label in parens, e.g. "Received Feedback (12)" — a count, not a badge, so it
   *  reads as part of the tab's name rather than a separate status signal. */
  count?: number;
}

export interface TabsProps {
  items: TabItem[];
  activeKey: string;
  onChange: (key: string) => void;
  className?: string;
}

/**
 * A horizontal tab strip — controlled, uncontrolled internal state is deliberately absent so a
 * host page can keep the active tab in the URL (`?tab=`) or component state as it prefers, the
 * same way `FilterStrip` in GoalsOverview.tsx does for its own local `role="tablist"` group. This
 * is that pattern promoted to `@hrms/ui` once a second call site (Feedback & 1:1s' four-tab home)
 * needed the identical shape — see the Feedback & 1:1s plan's "Web implementation" section.
 *
 * Underline-style rather than pill-style, so it reads as "one page, four views" rather than four
 * separate filter buttons — the visual distinction `FilterStrip` doesn't need (it's a single-row
 * filter, not a page-level navigation).
 */
export function Tabs({ items, activeKey, onChange, className }: TabsProps) {
  return (
    <div
      role="tablist"
      className={cn(
        'flex gap-4 sm:gap-6 overflow-x-auto scrollbar-none border-b border-border',
        className,
      )}
    >
      {items.map((item) => {
        const isActive = item.key === activeKey;
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(item.key)}
            className={cn(
              '-mb-px shrink-0 flex items-center min-h-[44px] whitespace-nowrap border-b-2 px-3.5 pb-3 pt-2 text-sm transition-colors touch-manipulation',
              isActive
                ? 'border-blue-700 font-bold text-blue-700 dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent font-medium text-slate-600 hover:border-slate-300 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100',
            )}
          >
            {item.label}
            {item.count !== undefined && (
              <span
                className={cn(
                  'ml-1.5 tabular-nums',
                  isActive ? 'text-blue-700 dark:text-blue-400' : 'text-slate-400',
                )}
              >
                ({item.count})
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
